import { FindProgramAccordionItemProps } from 'evcharging/containers/ev-rebates/FindProgramAccordionItem';
import {
  ActiveCategoryGroup,
  BlogPost,
  CategoryData,
  CmsLoginRequest,
  CmsLoginResponse,
  CreatePostCommentRequest,
  PageInterface,
  PostCategory,
  PostCollection,
  PostCommentsCollection,
  RssFeed,
  SitemapItem,
} from 'evcharging/services/types';
import { HttpMethod } from 'evcharging/app/http';
import { ENV, SITEMAP_PAGE_SIZE } from 'evcharging/app';
import { BLOG_CATEGORY_URL } from 'evcharging/app/seo/urls';
import { getRandomInt } from 'evcharging/utils/maths';

const CMS_URL = 'https://admin.energy5.com';
const LOGIN_URL = CMS_URL + '/auth/login';
const RESOURCE_BASE_URL = CMS_URL + '/items';

const STATES_COLLECTION = 'evc_states';
const STATES_QUERY = (locale: string) =>
  `fields=*,utilities.*,rebates.*&filter={"locale":{"_eq":"${locale}"}}`;

const PAGES_COLLECTION = 'evc_pages';
const PAGES_QUERY = (link: string) => `fields=*,faqs.*&filter={"link":{"_eq":"${link}"}}`;
const PAGES_SITEMAP_QUERY = `fields=link,lastmod,locale&filter={"external":{"_eq":false}}`;

export const BLOG_PAGE_SIZE = 29;
export const BLOG_All_ARTICLES_PAGE_SIZE = 15;
export const BLOG_ARTICLES_SLIDER_SIZE = 30;
export const BLOG_COMMENTS_PAGE_SIZE = 15;
export const BLOG_RELATED_ARTICLES_SIZE = 12;
export enum BLOG_CATEGORIES {
  trending = 'trending',
  guides = 'guides',
  articles = 'articles',
}

const BLOG_COLLECTION = 'evc_blog';
const BLOG_BY_LINK_QUERY = (link: string) =>
  `fields=*,active_category.*,faqs.*,sections.*,categories.evc_blog_categories_id.*&filter={"publish_at":{"_lte":"now"},"link":{"_eq":"${link}"}}`;
const BLOG_BY_POST_IDS_QUERY = (
  ids: string[],
  limit: number,
  offset = 0,
  activeCategoryId = ''
) => {
  const parts = [];
  if (ids.length) {
    parts.push({ id: { _in: ids } });
  }
  if (activeCategoryId) {
    parts.push({ active_category: { _eq: activeCategoryId } });
  }
  const condition = JSON.stringify(parts);
  return `fields=*,active_category.*,faqs.*,sections.*,categories.evc_blog_categories_id.*&meta=*&filter={"_or":${condition},"status":{"_eq":"published"},"publish_at": {"_lte":"now"}}&sort=-publish_at&limit=${limit}&offset=${offset}`;
};
const POST_IDS_QUERY = (ids: string[], activeCategoryId = '') => {
  const parts = [];
  if (ids.length) {
    parts.push({ id: { _in: ids } });
  }
  if (activeCategoryId) {
    parts.push({ active_category: { _eq: activeCategoryId } });
  }
  const condition = JSON.stringify(parts);
  return `fields=id&filter={"_or":${condition},"status":{"_eq":"published"},"publish_at": {"_lte":"now"}}&sort=id`;
};

const BLOG_BELONGS_TO_CATEGORIES_COLLECTION = 'evc_blog_evc_blog_categories';
const BLOG_RELATED_POST_IDS_QUERY = (postId: string, categoryIds: string[]) =>
  `fields=evc_blog_id&filter={"evc_blog_categories_id":{"_in":${JSON.stringify(
    categoryIds
  )}},"evc_blog_id":{"_neq":${postId}}}`;
const BLOG_SITEMAP_QUERY = (limit: number, offset: number, sort: string) =>
  `fields=link,locale,date_updated,publish_at,title,image,summary&filter={"status":{"_eq":"published"},"publish_at": {"_lte":"now"}}&limit=${limit}&offset=${offset}&sort=${sort}`;
const BLOG_RSS_QUERY = (locale: string) =>
  `fields=link,locale,date_updated,publish_at,title,image,summary&filter={"locale":{"_eq":"${locale}"},"status":{"_eq":"published"},"publish_at": {"_lte":"now"}}&limit=200&sort=-publish_at`;
const BLOG_CATEGORIES_LINKS_QUERY = `fields=id,link,locale&limit=10000&sort=id`;
const BLOG_SITEMAP_LINKS_QUERY = `aggregate[max]=publish_at,date_updated&groupBy=active_category`;
const BLOG_SITEMAP_COUNT_QUERY = `aggregate[count]=*&filter={"status":{"_eq":"published"},"publish_at": {"_lte":"now"}}`;

const BLOG_CATEGORIES_COLLECTION = 'evc_blog_categories';
const BLOG_LISTED_CATEGORIES_QUERY = (locale: string) =>
  `filter={"listed":{"_eq":true},"locale":{"_eq":"${locale}"}}&sort=sort`;
const BLOG_ALL_CATEGORIES_QUERY = (locale: string) =>
  `sort=sort&filter={"locale":{"_eq":"${locale}"}}`;
const BLOG_CATEGORY_BY_LINK_QUERY = (link: string) => `filter={"link":{"_eq":"${link}"}}`;
const BLOG_BY_CATEGORY_ID_QUERY = (categoryId: string) =>
  `fields=evc_blog_id&filter={"evc_blog_categories_id":{"_eq":${categoryId}},"evc_blog_id":{"_neq":null}}&limit=10000`;

const BLOG_BELONGS_TO_PAGES_COLLECTION = 'evc_blog_evc_pages';
const BLOG_PAGE_RELATED_POST_IDS_QUERY = (pageIds: string[]) =>
  `fields=evc_blog_id&filter={"evc_pages_id":{"_in":${JSON.stringify(pageIds)}}}`;

const POST_COMMENTS_COLLECTION = 'evc_blog_comments';
const POST_COMMENTS_BY_POST_ID_QUERY = (postId: string, limit: number, offset = 0) =>
  `fields=*&meta=*&filter={"blog_id":{"_eq": ${postId}}}&sort=-publish_at&limit=${limit}&offset=${offset}`;

const resourceUrl = (collection: string, query = '') =>
  `${RESOURCE_BASE_URL}/${collection}${query ? `?${query}` : ''}`;

const assetUrl = (id: string) => `https://admin.energy5.com/assets/${id}`;

export const getRebates = async (locale: string): Promise<FindProgramAccordionItemProps[]> => {
  const response = await fetch(resourceUrl(STATES_COLLECTION, STATES_QUERY(locale)));
  if (response.ok) {
    const { data } = await response.json();
    return data.map((state: Record<string, any>) => ({
      title: `${state.code}, ${state.name}`,
      body: [
        ...state.rebates.map((rebate: Record<string, any>, index: number) => ({
          header: index ? null : 'rebates.state',
          title: rebate.name,
          body: rebate.text,
        })),
        ...state.utilities.map((rebate: Record<string, any>, index: number) => ({
          header: index ? null : 'rebates.utility',
          title: rebate.name,
          body: rebate.text,
        })),
      ],
    }));
  }
  return [];
};

export const getPage = async (link: string, locale: string): Promise<PageInterface> => {
  let locales = [];
  let response = await fetch(resourceUrl(PAGES_COLLECTION, PAGES_QUERY(link)));
  if (response.ok) {
    const { data } = await response.json();
    const page = data.find((item: Record<string, unknown>) => item.locale === locale);
    locales = data.map((item: Record<string, unknown>) => item.locale);
    if (page) {
      response = await fetch(
        resourceUrl(BLOG_BELONGS_TO_PAGES_COLLECTION, BLOG_PAGE_RELATED_POST_IDS_QUERY(page.id))
      );
      if (response.ok) {
        const { data } = await response.json();
        if (data.length) {
          response = await fetch(
            resourceUrl(
              BLOG_COLLECTION,
              BLOG_BY_POST_IDS_QUERY(
                data.map(({ evc_blog_id }: any) => evc_blog_id),
                BLOG_RELATED_ARTICLES_SIZE
              )
            )
          );
          if (response.ok) {
            const { data: records } = await response.json();
            return { ...page, posts: records.map(transformBlogPost), locales };
          }
        }
      }
    }
  }
  return { id: '', faqs: [], posts: [], locales };
};

export const getBlogListedCategories = async (locale: string): Promise<PostCategory[]> => {
  const response = await fetch(
    resourceUrl(BLOG_CATEGORIES_COLLECTION, BLOG_LISTED_CATEGORIES_QUERY(locale))
  );
  if (response.ok) {
    const { data } = await response.json();
    return data;
  }
  return [];
};

export const getBlogAllCategories = async (locale: string): Promise<PostCategory[]> => {
  const response = await fetch(
    resourceUrl(BLOG_CATEGORIES_COLLECTION, BLOG_ALL_CATEGORIES_QUERY(locale))
  );
  if (response.ok) {
    const { data } = await response.json();
    return data;
  }
  return [];
};

export const getBlogCategory = async (
  link: string,
  locale: string
): Promise<PostCategory | null> => {
  const response = await fetch(
    resourceUrl(BLOG_CATEGORIES_COLLECTION, BLOG_CATEGORY_BY_LINK_QUERY(link))
  );
  if (response.ok) {
    const { data } = await response.json();
    const category = data.find((item: Record<string, unknown>) => item.locale === locale);

    return category
      ? {
          ...category,
          locales: data.map((item: Record<string, unknown>) => item.locale),
        }
      : null;
  }
  return null;
};

export const getPostsByCategory = async (
  locale: string,
  categoryId: string,
  page: number,
  size = BLOG_PAGE_SIZE,
  asOffset = false
): Promise<PostCollection> => {
  let response = await fetch(
    resourceUrl(BLOG_BELONGS_TO_CATEGORIES_COLLECTION, BLOG_BY_CATEGORY_ID_QUERY(categoryId))
  );

  if (response.ok) {
    const { data } = await response.json();
    const postIds = data
      .map((item: Record<string, any>) => item.evc_blog_id)
      .filter((postId: number, i: number, a: number[]) => a.indexOf(postId) === i);

    response = await fetch(
      resourceUrl(
        BLOG_COLLECTION,
        BLOG_BY_POST_IDS_QUERY(postIds, size, asOffset ? page : (page - 1) * size, categoryId)
      )
    );
    if (response.ok) {
      const { data: records, meta } = await response.json();
      return { data: records.map(transformBlogPost), total: meta.filter_count };
    }
  }
  return { data: [], total: 0 };
};

export const getBlogPost = async (link: string, locale: string): Promise<BlogPost | null> => {
  const response = await fetch(resourceUrl(BLOG_COLLECTION, BLOG_BY_LINK_QUERY(link)));
  if (response.ok) {
    const { data } = await response.json();
    const blog = data.find((item: Record<string, unknown>) => item.locale === locale);
    return blog
      ? transformBlogPost({
          ...blog,
          locales: data.map((item: Record<string, unknown>) => item.locale),
        })
      : null;
  }
  return null;
};

export const getRelatedArticles = async (post: BlogPost): Promise<BlogPost[]> => {
  const categoryIds = post.categories.map((category) => category.id);
  if (post.active_category?.id) {
    categoryIds.push(post.active_category.id);
  }
  let response = await fetch(
    resourceUrl(
      BLOG_BELONGS_TO_CATEGORIES_COLLECTION,
      BLOG_RELATED_POST_IDS_QUERY(post.id, categoryIds)
    )
  );
  if (response.ok) {
    const { data } = await response.json();
    const postIds = data
      .map((item: Record<string, any>) => item.evc_blog_id)
      .filter((postId: number, i: number, a: number[]) => a.indexOf(postId) === i);

    const offset = await calculatedArticlesOffset(post, postIds);
    response = await fetch(
      resourceUrl(
        BLOG_COLLECTION,
        BLOG_BY_POST_IDS_QUERY(
          postIds,
          BLOG_RELATED_ARTICLES_SIZE,
          offset,
          post.active_category?.id
        )
      )
    );
    if (response.ok) {
      const { data: records } = await response.json();
      return records.filter((blogPost: BlogPost) => blogPost.id !== post.id).map(transformBlogPost);
    }
  }
  return [];
};

const calculatedArticlesOffset = async (post: BlogPost, postIds: string[]): Promise<number> => {
  const response = await fetch(
    resourceUrl(BLOG_COLLECTION, POST_IDS_QUERY([...postIds, post.id], post.active_category?.id))
  );
  if (response.ok) {
    const { data } = await response.json();
    const postIndex = data.map((blogPost: BlogPost) => blogPost.id).indexOf(post.id);

    return postIndex >= 0 && postIndex + BLOG_RELATED_ARTICLES_SIZE < data.length ? postIndex : 0;
  }
  return 0;
};

export const getCategoryRandomOffset = async (
  link: string,
  size: number,
  locale: string
): Promise<number> => {
  const category = await getBlogCategory(link, locale);
  if (!category) {
    return 1;
  }
  const collection = await getPostsByCategory(locale, category.id, 1, 1);
  return getRandomInt(0, collection.total - size);
};

export const getCategoryByLink = async (
  link: string,
  page: number,
  size: number,
  locale: string
): Promise<CategoryData | null> => {
  const category = await getBlogCategory(link, locale);
  if (!category) {
    return null;
  }
  const collection = await getPostsByCategory(locale, category.id, page, size);

  return { category, collection };
};

export const getCategoryByLinkRandom = async (
  link: string,
  size: number,
  locale: string
): Promise<CategoryData | null> => {
  const category = await getBlogCategory(link, locale);
  if (!category) {
    return null;
  }
  const randomOffset = await getCategoryRandomOffset(link, size, locale);
  const collection = await getPostsByCategory(locale, category.id, randomOffset, size, true);

  return { category, collection };
};

const withLocales = (items: SitemapItem[]) => {
  const locales: Record<string, string[]> = {};
  items.forEach((item) => {
    if (!locales[item.link]) {
      locales[item.link] = [];
    }
    if (!locales[item.link].includes(item.locale)) {
      locales[item.link].push(item.locale);
    }
    item.locales = locales[item.link];
  });

  return items;
};

export const getSitemapPages = async (): Promise<SitemapItem[]> => {
  const response = await fetch(resourceUrl(PAGES_COLLECTION, PAGES_SITEMAP_QUERY));
  if (response.ok) {
    const { data } = await response.json();

    return withLocales(data);
  }
  return [];
};

export const getSitemapCategories = async (): Promise<SitemapItem[]> => {
  let response = await fetch(resourceUrl(BLOG_CATEGORIES_COLLECTION, BLOG_CATEGORIES_LINKS_QUERY));
  if (response.ok) {
    const { data } = await response.json();
    response = await fetch(resourceUrl(BLOG_COLLECTION, BLOG_SITEMAP_LINKS_QUERY));
    if (response.ok) {
      const { data: groups } = await response.json();
      const lastmods = groups.reduce(
        (accum: Record<string, string>, group: ActiveCategoryGroup) => {
          const {
            active_category,
            max: { publish_at, date_updated },
          } = group;
          accum[active_category] = date_updated || publish_at;
          return accum;
        },
        {}
      );

      return withLocales(
        data.map((item: SitemapItem & { id: string }) => ({
          ...item,
          link: BLOG_CATEGORY_URL(item.link),
          lastmod: lastmods[item.id],
        }))
      );
    }
  }
  return [];
};

export const getRssFeed = async (locale: string): Promise<RssFeed[]> => {
  const response = await fetch(resourceUrl(BLOG_COLLECTION, BLOG_RSS_QUERY(locale)));

  if (response.ok) {
    const { data } = await response.json();

    return data.map((post: BlogPost) => ({
      ...post,
      description: post.meta_description,
      link: `/${post.link}`,
      lastmod: post.date_updated || post.publish_at,
      image: assetUrl(post.image),
    }));
  }
  return [];
};

export const getSitemapPosts = async (
  page: number,
  size = SITEMAP_PAGE_SIZE,
  sort = 'publish_at'
): Promise<SitemapItem[]> => {
  const response = await fetch(
    resourceUrl(BLOG_COLLECTION, BLOG_SITEMAP_QUERY(size, (page - 1) * size, sort))
  );
  if (response.ok) {
    const { data } = await response.json();
    return withLocales(
      data.map(
        ({
          link,
          locale,
          date_updated,
          publish_at,
          image,
          title,
          summary,
          meta_description,
        }: BlogPost) => {
          return {
            title,
            description: meta_description,
            locale,
            link: `/${link}`,
            lastmod: date_updated || publish_at,
            images: [
              {
                src: assetUrl(image),
                title:
                  title && typeof title.replaceAll === 'function'
                    ? title.replaceAll('&', '')
                    : 'Turnkey EV Charging Solutions',
              },
              ...extractImages(summary || '', title),
            ],
          };
        }
      )
    );
  }
  return [];
};

export const getSitemapPostsPagesTotal = async (): Promise<number> => {
  const response = await fetch(resourceUrl(BLOG_COLLECTION, BLOG_SITEMAP_COUNT_QUERY));
  if (response.ok) {
    const { data } = await response.json();
    const count = data[0].count;
    return Math.ceil(count / SITEMAP_PAGE_SIZE);
  }

  return 0;
};

const extractImages = (html: string, title: string) => {
  const images = html.match(/<img [^>]*src="[^"]*"[^>]*>/gm);
  return images
    ? images
        .map((img) => ({
          src: img.replace(/.*src="([^"]*)".*/, '$1').trim(),
          title: img.replace(/.*title="([^"]*)".*/, '$1').replace('&', '') || title,
        }))
        .filter((img) => !!img.src)
    : [];
};

export const createPostComment = async (data: CreatePostCommentRequest): Promise<void> => {
  await fetch(resourceUrl(POST_COMMENTS_COLLECTION), {
    method: HttpMethod.POST,
    headers: {
      Authorization: `Bearer ${ENV.POST_COMMENTER_STATIC_TOKEN}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
};

export const getPostComments = async (
  postId: string,
  page = 1,
  size = BLOG_COMMENTS_PAGE_SIZE
): Promise<PostCommentsCollection> => {
  const response = await fetch(
    resourceUrl(
      POST_COMMENTS_COLLECTION,
      POST_COMMENTS_BY_POST_ID_QUERY(postId, size, (page - 1) * size)
    )
  );
  if (response.ok) {
    const { data, meta } = await response.json();
    return { data, total: meta.filter_count };
  }
  return { data: [], total: 0 };
};

const login = async (data: CmsLoginRequest): Promise<CmsLoginResponse | undefined> => {
  const response = await fetch(LOGIN_URL, {
    method: HttpMethod.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (response.ok) {
    const { data } = await response.json();
    return data;
  }
};

const transformBlogPost = (data: Record<string, any>): BlogPost => {
  const categories = data.categories.map(
    (item: Record<string, any>) => item.evc_blog_categories_id
  );
  const sections = data.sections.map((item: Record<string, any>) => ({
    ...item,
    image: item.image ? assetUrl(item.image) : null,
  }));
  const active_category = data.active_category
    ? data.active_category
    : categories.find((category: any) => category.listed) || categories[0];

  return {
    ...data,
    categories,
    sections,
    image: assetUrl(data.image),
    active_category: active_category || {},
  } as BlogPost;
};
